import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import { Gift, Plus, Trash2, Percent, Save } from 'lucide-react';

export function BulkDiscountSettings({ 
  data, 
  setData, 
  isSaving, 
  onSave,
  addTier,
  updateTier,
  removeTier
}) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-bold text-slate-900 font-[Poppins]">Bulk Booking Discount</h3>
          <p className="text-sm text-slate-500 mt-1">Offer discounts when customers book multiple units</p>
        </div>
        <div className="flex items-center gap-3">
          <Label htmlFor="bulk-discount-enabled" className="text-sm">Enable Discounts</Label>
          <Switch
            id="bulk-discount-enabled"
            checked={data.enabled}
            onCheckedChange={(checked) => setData(prev => ({ ...prev, enabled: checked }))}
            data-testid="bulk-discount-enabled-switch"
          />
        </div>
      </div>

      {/* Discount Tiers */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-slate-700">Discount Tiers</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addTier}
            data-testid="add-discount-tier-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Tier
          </Button>
        </div>

        {data.tiers.length === 0 ? (
          <div className="text-center py-8 text-slate-400 border-2 border-dashed border-stone-200 rounded-xl">
            <Percent className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No discount tiers configured</p>
            <p className="text-xs mt-1">Add tiers to offer bulk booking discounts</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.tiers.map((tier, index) => (
              <div key={`tier-${tier.min_units}-${tier.discount_percentage}`} className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Minimum Units</Label>
                    <Input
                      type="number"
                      min="2"
                      max="10"
                      value={tier.min_units}
                      onChange={(e) => updateTier(index, 'min_units', parseInt(e.target.value) || 2)}
                      className="mt-1 h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Discount (%)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="50"
                      value={tier.discount_percentage}
                      onChange={(e) => updateTier(index, 'discount_percentage', parseInt(e.target.value) || 0)}
                      className="mt-1 h-10"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTier(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Preview */}
        {data.enabled && data.tiers.length > 0 && (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <h4 className="font-medium text-amber-900 mb-2 flex items-center gap-2">
              <Gift className="w-4 h-4" />
              Customer Preview
            </h4>
            <ul className="text-sm text-amber-800 space-y-1">
              {data.tiers
                .sort((a, b) => a.min_units - b.min_units)
                .map((tier) => (
                  <li key={`preview-${tier.min_units}`}>
                    • Book {tier.min_units}+ units → Get <strong>{tier.discount_percentage}% off</strong>
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>

      <Button
        onClick={onSave}
        disabled={isSaving}
        className="mt-6 w-full sm:w-auto"
        data-testid="save-bulk-discount-btn"
      >
        <Save className="w-4 h-4 mr-2" />
        {isSaving ? 'Saving...' : 'Save Bulk Discount Settings'}
      </Button>
    </div>
  );
}
